import { createBrowserHistory } from "history";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import WithLocale from "@Components/WithLocale";
import useCheerzApiProvider from "@Hooks/useCheerzApiProvider";
import { ReactComponent as ChevronLeft } from "@Icons/chevron-left.svg";
import { ReactComponent as ChevronRight } from "@Icons/chevron-right.svg";
import { Banner, BannerKountries as BannerKountriesType } from "@Types/bannerKountries";
import { convertFromApiToBannerKountries } from "@Utils/bannerKountries";
import { setDataLayerTrack } from "@Utils/track";
import "./style.scss";

const history = createBrowserHistory();

interface Props {
  backgroundColor: string;
  banners: Banner[];
}

export const BannerKountries: FunctionComponent<Props> = ({ banners, backgroundColor }) => {
  const intl = useIntl();

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [scrollPercentage, setScrollPercentage] = useState<number>(0);

  const scrollLeft = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setScrollPercentage(scrollPercentage + 100);
    } else {
      setCurrentStep(banners.length);
      setScrollPercentage(-(banners.length - 1) * 100);
    }
  };

  const scrollRight = () => {
    if (currentStep < banners.length) {
      setCurrentStep(currentStep + 1);
      setScrollPercentage(scrollPercentage - 100);
    } else {
      setCurrentStep(1);
      setScrollPercentage(0);
    }
  };

  useEffect(() => {
    setDataLayerTrack("Promotion Viewed", {
      category: "Menu",
      message: banners[currentStep - 1].message,
      position: "web_banner",
    });
  }, [currentStep]);

  return (
    <div className="BannerKountries" style={{ backgroundColor }}>
      {banners.length > 1 && (
        <div className="BannerKountries-chevronWrapper-left" onClick={scrollLeft}>
          <ChevronLeft />
        </div>
      )}
      <div className="BannerKountries-wrapper">
        <div
          className="BannerKountries-scrollWrapper"
          style={{ transform: `translateX(${scrollPercentage}%)` }}
        >
          {banners.map((banner, index) => {
            return (
              <div className="BannerKountries-item" key={index}>
                {banner.link !== "" ? (
                  <a
                    className="BannerKountries-item-message"
                    href={banner.link}
                    onClick={() =>
                      setDataLayerTrack("Promotion Clicked", {
                        category: "Menu",
                        message: banner.message,
                        position: "web_banner",
                      })
                    }
                  >
                    <span>{banner.message}</span>
                    <span className="BannerKountries-item-link">
                      | <span>{intl.formatMessage({ id: "cheerz_BannerKountries_more" })}</span>
                    </span>
                  </a>
                ) : (
                  <span className="BannerKountries-item-message">{banner.message}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {banners.length > 1 && (
        <div className="BannerKountries-chevronWrapper-right" onClick={scrollRight}>
          <ChevronRight />
        </div>
      )}
    </div>
  );
};

const BannerKountriesContainer: FunctionComponent = () => {
  const [bannersInfos, setBannersInfos] = useState<BannerKountriesType>();

  const cheerzApiProvider = useCheerzApiProvider();

  useEffect(() => {
    cheerzApiProvider
      .getBanners()
      .then((data) => setBannersInfos(convertFromApiToBannerKountries(data)))
      .catch(() => {
        return;
      });
  }, [cheerzApiProvider]);

  return (
    <div className="BannerKountriesContainer">
      {bannersInfos && bannersInfos.banners.length > 0 ? (
        <WithLocale history={history}>
          <BannerKountries
            banners={bannersInfos.banners}
            backgroundColor={bannersInfos.backgroundColor}
          />
        </WithLocale>
      ) : null}
    </div>
  );
};

export default BannerKountriesContainer;
